import { Actor, Color, Collider, CollisionContact, Engine, Font, FontUnit, Label, Side, vec, Vector } from "excalibur";
import { gameState } from "./gameState";
import { tower } from "./tower";

export type EnemyType = 'normal' | 'heavy' | 'ranged' | 'fast' | 'boss';

export interface EnemyStats {
  maxHp: number;
  speed: number; // pixels per second
  attack: number; // damage per hit
  range: number; // for ranged, 0 = melee
  money: number; // gold reward
}

// Global registry of live enemies to avoid expensive scene-wide filters every frame
export const liveEnemies = new Set<Enemy>();

export class Enemy extends Actor {
  type: EnemyType;
  stats: EnemyStats;
  hp: number;
  attackCooldown = 0;
  attackRate = 1.0; // per second
  label: Label;
  private _hpLabelTimer = 0; // ms accumulator for throttled label updates
  private _lastHpShown = -1;

  constructor(type: EnemyType, stats: EnemyStats, pos: Vector) {
    super({ name: `enemy-${type}` , pos, width: 16, height: 16, color: Enemy.colorFor(type) });
    this.type = type;
    this.stats = stats;
    this.hp = stats.maxHp;
    this.label = new Label({ text: '', pos: vec(0, -12), color: Color.White, font: new Font({ size: 8, unit: FontUnit.Px })});
    this.addChild(this.label);
    this.tags.add('enemy');

    // Track in global registry
    this.on('initialize', () => liveEnemies.add(this));
    this.on('kill', () => liveEnemies.delete(this));
  }

  static colorFor(type: EnemyType) {
    switch(type) {
      case 'normal': return Color.Chartreuse;
      case 'heavy': return Color.DarkGray;
      case 'ranged': return Color.Cyan;
      case 'fast': return Color.Orange;
      case 'boss': return Color.Red;
    }
  }

  takeDamage(amount: number) {
    this.hp -= amount;
    if (this.hp <= 0) {
      this.kill();
      gameState.addGold(this.stats.money);
    }
  }

  override onPreUpdate(engine: Engine, elapsedMs: number): void {
    // Simple AI: move towards tower
    if (!tower.isKilled()) {
      const dir = tower.pos.sub(this.pos);
      const dist = dir.size;
      // Do not allow enemies to enter the tower: keep at least combined radii apart
      const minDist = (this.width / 2) + (tower.width / 2) + 2; // +2 padding

      if (this.stats.range > 0 && dist <= this.stats.range) {
        // ranged attack
        this.attackCooldown -= elapsedMs / 1000;
        if (this.attackCooldown <= 0) {
          tower.takeDamage(this.stats.attack);
          this.attackCooldown = 1 / this.attackRate;
        }
        // maintain distance, small circle
        const tangent = vec(-dir.y, dir.x).normalize().scale(this.stats.speed * (elapsedMs/1000));
        this.pos = this.pos.add(tangent);
      } else if (dist > minDist) {
        // Move toward tower but do not overshoot the stop radius
        const dt = (elapsedMs / 1000);
        const maxStep = this.stats.speed * dt;
        const needed = Math.max(0, dist - minDist);
        const stepLen = Math.min(maxStep, needed);
        this.pos = this.pos.add(dir.normalize().scale(stepLen));
      } else {
        // melee (at the edge of the tower), do not move inside
        this.attackCooldown -= elapsedMs / 1000;
        if (this.attackCooldown <= 0) {
          tower.takeDamage(this.stats.attack);
          this.attackCooldown = 1 / this.attackRate;
        }
      }
    }

    // Throttle HP label updates to ~5 times per second and only on change
    this._hpLabelTimer += elapsedMs;
    const hpCeil = Math.ceil(this.hp);
    if (hpCeil !== this._lastHpShown || this._hpLabelTimer >= 200) {
      this._hpLabelTimer = 0;
      this._lastHpShown = hpCeil;
      this.label.text = `${hpCeil}`;
    }
  }

  override onCollisionStart(self: Collider, other: Collider, side: Side, contact: CollisionContact): void {
    // not used for now
  }
}

export function statsFor(type: EnemyType, baseHP: number, baseMoney: number): EnemyStats {
  switch(type) {
    case 'normal': return { maxHp: baseHP, speed: 30, attack: 3, range: 0, money: baseMoney };
    case 'heavy': return { maxHp: baseHP * 2.5, speed: 20, attack: 5, range: 0, money: Math.floor(baseMoney*1.5) };
    case 'ranged': return { maxHp: baseHP * 0.5, speed: 25, attack: 2, range: 120, money: baseMoney };
    case 'fast': return { maxHp: baseHP * 0.5, speed: 60, attack: 2, range: 0, money: baseMoney };
    case 'boss': return { maxHp: baseHP * 20, speed: 15, attack: 15, range: 0, money: Math.floor(baseMoney*10) };
  }
}
